/**
 * Checkout credit picks — how much of each picked credit can go on this cart.
 *
 * Mirrors the server's rule (ClientCreditService.ApplyAtCheckoutAsync) so the amounts shown are
 * what will apply: a credit covers at most its remaining balance and whatever the cart total still
 * has room for, and by type —
 *   Brand (1):       only its brand's product in the cart (fees excluded) — and a house brand's credit
 *                    only what Stash House credits picked before it left of the house-brand product;
 *   Stash House (2): only house-brand product in the cart and those lines' unit fees;
 *   Overpay (3):     anything.
 * No credit of any type covers a sample: brandTotals leaves them out, and callers pass the cart total
 * less samples (useCart's creditableCartTotal) as cartTotal.
 * Credits are counted in the order given — the order the customer picked them, as the server applies
 * them. The server re-checks at submit and applies what fits, so these figures guide the customer;
 * they are never trusted.
 */

export const CREDIT_TYPE = { BRAND: 1, STASH_HOUSE: 2, OVERPAY: 3 };

const round2 = (value) => Math.round(value * 100) / 100;

/** Brand credits first, then Stash House, then Overpay — the default order credits are offered in. */
export const byTypeOrder = (a, b) => (a.creditTypeId || 0) - (b.creditTypeId || 0);

/**
 * @param {Array} credits available credits ({ id, creditTypeId, brandId, remainingAmount }), in the
 *   order they're applied
 * @param {Object} entered picked credits: { [creditId]: amount wanted (number) }
 * @param {number} cartTotal the cart's total (products + fees) less samples
 * @param {Object} brandTotals { [brandId]: { product, withFees } } money in the cart per brand
 * @param {Array<number>} houseBrandIds the house brands, which a Stash House credit can cover
 * @returns {{ amounts: Object, maxFor: Function, total: number }} amounts = what each picked
 *   credit will apply; maxFor(credit) = the most that credit could take given the other picks.
 */
export const resolveCreditPicks = (credits, entered, cartTotal, brandTotals, houseBrandIds = []) => {
  const house = new Set(houseBrandIds);
  const brandProduct = (brandId) => brandTotals[brandId]?.product || 0;
  // The part of the cart a Stash House credit can cover: house-brand product and its unit fees.
  const houseTotal = houseBrandIds.reduce((sum, brandId) => sum + (brandTotals[brandId]?.withFees || 0), 0);
  // Whether a credit uses up house-brand room (a Stash House credit, or a house brand's own credit).
  const usesHouseRoom = (credit) =>
    credit.creditTypeId === CREDIT_TYPE.STASH_HOUSE || (credit.brandId != null && house.has(credit.brandId));

  let orderRoom = cartTotal;
  let houseRoom = houseTotal;
  const brandRoom = {};
  const amounts = {};

  credits.forEach((credit) => {
    if (entered[credit.id] == null) return;
    let max = Math.min(credit.remainingAmount, orderRoom);
    if (credit.creditTypeId === CREDIT_TYPE.BRAND)
      max = Math.min(max, brandRoom[credit.brandId] ?? brandProduct(credit.brandId));
    if (usesHouseRoom(credit)) max = Math.min(max, houseRoom);

    const amount = round2(Math.max(0, Math.min(entered[credit.id], max)));
    amounts[credit.id] = amount;
    orderRoom -= amount;
    if (usesHouseRoom(credit)) houseRoom -= amount;
    if (credit.creditTypeId === CREDIT_TYPE.BRAND)
      brandRoom[credit.brandId] = (brandRoom[credit.brandId] ?? brandProduct(credit.brandId)) - amount;
  });

  const others = (credit, predicate) =>
    credits
      .filter((c) => c.id !== credit.id && amounts[c.id] != null && predicate(c))
      .reduce((sum, c) => sum + amounts[c.id], 0);

  const maxFor = (credit) => {
    let max = Math.min(credit.remainingAmount, cartTotal - others(credit, () => true));
    if (credit.creditTypeId === CREDIT_TYPE.BRAND)
      max = Math.min(max, brandProduct(credit.brandId) - others(credit, (c) => c.brandId === credit.brandId));
    if (usesHouseRoom(credit)) max = Math.min(max, houseTotal - others(credit, usesHouseRoom));
    return round2(Math.max(0, max));
  };

  const total = round2(Object.values(amounts).reduce((sum, a) => sum + a, 0));
  return { amounts, maxFor, total };
};

/**
 * The most the store's credits could take off this cart if every one were used in full, under the
 * same limits as checkout, Brand credits first. For the "you have credits you can use" note in the cart.
 */
export const usableCreditTotal = (credits, cartTotal, brandTotals, houseBrandIds = []) =>
  resolveCreditPicks(
    [...credits].sort(byTypeOrder),
    Object.fromEntries(credits.map((c) => [c.id, c.remainingAmount])),
    cartTotal,
    brandTotals,
    houseBrandIds,
  ).total;
